import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import JSZip from "jszip";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Database,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Info,
  ShieldCheck,
  Loader2,
  PackageOpen,
  Layers,
  Zap,
} from "lucide-react";

const numberFmt = (n) => new Intl.NumberFormat().format(Number(n || 0));

const DownloadPartCard = ({ part, title, subtitle, icon: Icon, tint, tables, records, downloading, busy, onDownload }) => {
  const busyCsv = downloading === `${part}.csv`;
  const busyJson = downloading === `${part}.json`;
  return (
    <Card className={`${tint} border`} data-testid={`backup-part-card-${part}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/70 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-600 mt-0.5">{subtitle}</p>
            <p className="text-sm text-slate-700 mt-2">
              <strong>{numberFmt(records)}</strong> records across{" "}
              <strong>{numberFmt(tables.length)}</strong> tables
            </p>
            {tables.length > 0 && (
              <p className="text-xs text-slate-500 mt-1 truncate" title={tables.map((t) => t.name).join(", ")}>
                {tables.slice(0, 3).map((t) => t.name).join(", ")}
                {tables.length > 3 ? ` +${tables.length - 3} more` : ""}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                onClick={() => onDownload("csv", part)}
                disabled={!!busy || tables.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                data-testid={`download-${part}-csv-btn`}
              >
                {busyCsv ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                CSV (Excel)
              </Button>
              <Button
                onClick={() => onDownload("json", part)}
                disabled={!!busy || tables.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                data-testid={`download-${part}-json-btn`}
              >
                {busyJson ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                JSON
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Backup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // e.g. "heavy.csv" | "light.json" | "all.csv"
  // Client-side multi-download orchestrator state (auto-assembled backup).
  // Keys we set: { active: bool, format, current: string, done: int, total: int, failed: [] }
  const [multi, setMulti] = useState({ active: false, format: null, current: "", done: 0, total: 0, failed: [] });

  const cancelMultiRef = useCallback(() => {
    // simple cancellation via a mutable flag on window — set by the Cancel button
    if (typeof window !== "undefined") window.__backupCancel = true;
  }, []);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API}/backup/summary`);
      setSummary(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load backup summary");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "super_admin") {
      toast.error("This page is restricted to Super Admin");
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchSummary();
  }, [user, navigate, fetchSummary]);

  const startDownload = async (format, part = "all") => {
    const key = `${part}.${format}`;
    setDownloading(key);
    try {
      const res = await axios.get(`${API}/backup/download`, {
        params: { format, part },
        responseType: "blob",
        timeout: 5 * 60 * 1000,
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const stamp = new Date().toISOString().slice(0, 10);
      const partSlug = part === "all" ? "full" : part;
      const filename = match ? match[1] : `locofast-backup-${stamp}.${partSlug}.${format}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const label = part === "all" ? "Full" : part === "heavy" ? "Part 1 (Heavy)" : "Part 2 (Light)";
      toast.success(`${label} backup downloaded (${format.toUpperCase()})`);
    } catch (err) {
      let detail = "Download failed";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          detail = JSON.parse(text).detail || detail;
        } catch { /* leave default */ }
      } else {
        detail = err.response?.data?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setDownloading(null);
    }
  };

  const startCollectionDownload = async (name, format) => {
    const key = `col.${name}.${format}`;
    setDownloading(key);
    try {
      const res = await axios.get(`${API}/backup/collection/${encodeURIComponent(name)}`, {
        params: { format },
        responseType: "blob",
        timeout: 5 * 60 * 1000,
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = match ? match[1] : `locofast-${name}-${stamp}.${format}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${name} (${format.toUpperCase()})`);
    } catch (err) {
      let detail = "Download failed";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          detail = JSON.parse(text).detail || detail;
        } catch { /* leave default */ }
      } else {
        detail = err.response?.data?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setDownloading(null);
    }
  };

  /**
   * AUTO-ASSEMBLED BACKUP
   * ---------------------
   * Downloads every collection one-by-one via /api/backup/collection/{name},
   * extracts the raw .json / .csv file from each mini-ZIP, and assembles them
   * into a single master ZIP client-side using JSZip.
   *
   * Each request is tiny + fast, so Cloudflare's ~100s per-request timeout
   * never applies. Total wall-clock time = sum(individual collection times).
   * A `_manifest.json` summarizing the run is added at the top of the master ZIP.
   */
  const startAutoAssembledBackup = async (format) => {
    if (multi.active) return;
    if (typeof window !== "undefined") window.__backupCancel = false;

    // Ensure we have a fresh table list
    let tables = summary?.tables || [];
    if (!tables.length) {
      try {
        const { data } = await axios.get(`${API}/backup/summary`);
        setSummary(data);
        tables = data?.tables || [];
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load collection list");
        return;
      }
    }

    setMulti({ active: true, format, current: "", done: 0, total: tables.length, failed: [] });
    const startedAt = new Date().toISOString();
    const master = new JSZip();
    const perCollection = [];
    const failures = [];

    for (let i = 0; i < tables.length; i++) {
      if (window.__backupCancel) {
        toast.message("Backup cancelled");
        break;
      }
      const t = tables[i];
      setMulti((m) => ({ ...m, current: t.name, done: i }));

      try {
        const res = await axios.get(`${API}/backup/collection/${encodeURIComponent(t.name)}`, {
          params: { format },
          responseType: "arraybuffer",
          timeout: 3 * 60 * 1000, // 3 min per collection is generous
        });
        // Unzip the per-collection response and grab the raw .json / .csv file inside.
        const innerZip = await JSZip.loadAsync(res.data);
        const dataFilename = `${t.name}.${format}`;
        const dataFile = innerZip.file(dataFilename);
        if (!dataFile) {
          throw new Error(`inner ZIP missing ${dataFilename}`);
        }
        const bytes = await dataFile.async("uint8array");
        master.file(dataFilename, bytes);

        // Also pull the per-collection manifest so we can compute totals accurately
        try {
          const inner = innerZip.file("_manifest.json");
          if (inner) {
            const m = JSON.parse(await inner.async("string"));
            perCollection.push({ name: t.name, estimated_count: m.estimated_count, actual_count: m.actual_count });
          } else {
            perCollection.push({ name: t.name });
          }
        } catch { /* non-fatal */ }
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "download failed";
        failures.push({ name: t.name, reason: String(detail) });
      }
    }

    // Early-return if cancelled — do NOT emit a partial ZIP download.
    if (window.__backupCancel) {
      setMulti({ active: false, format: null, current: "", done: 0, total: 0, failed: [] });
      window.__backupCancel = false;
      return;
    }

    // Write master manifest describing the whole assembled backup
    const totalActual = perCollection.reduce((s, c) => s + (c.actual_count || 0), 0);
    const masterManifest = {
      generated_at: startedAt,
      completed_at: new Date().toISOString(),
      format,
      strategy: "client-side auto-assembled from per-collection endpoints",
      total_tables_requested: tables.length,
      total_tables_included: perCollection.length,
      total_records: totalActual,
      per_collection: perCollection,
      failures,
    };
    master.file("_manifest.json", JSON.stringify(masterManifest, null, 2));

    // Emit the master ZIP as a browser download
    try {
      const blob = await master.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "").replace(/-/g, "").slice(0, 15);
      const filename = `locofast-backup-${stamp}.assembled.${format}.zip`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const okCount = perCollection.length;
      if (failures.length === 0) {
        toast.success(`Complete backup assembled — ${okCount} tables, ${numberFmt(totalActual)} records (${format.toUpperCase()})`);
      } else {
        toast.warning(`Assembled with ${failures.length} failure${failures.length !== 1 ? "s" : ""} — see _manifest.json inside the ZIP`);
      }
    } catch (err) {
      toast.error(`Failed to assemble master ZIP: ${err.message}`);
    } finally {
      setMulti({ active: false, format: null, current: "", done: 0, total: 0, failed: failures });
      if (typeof window !== "undefined") window.__backupCancel = false;
    }
  };

  const totalRecords = summary?.total_records ?? 0;
  const totalTables = summary?.total_tables ?? (summary?.tables?.length || 0);
  const heavyTables = summary?.heavy_tables || [];
  const lightTables = summary?.light_tables || [];
  const heavyRecords = summary?.heavy_total_records ?? 0;
  const lightRecords = summary?.light_total_records ?? 0;
  const heavyThreshold = summary?.heavy_threshold ?? 1000;
  const hasSplit = heavyTables.length > 0;

  // Single source of truth for "any download in flight" — used to disable
  // every other download control so users can't race two operations at once.
  const busy = !!downloading || multi.active;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="backup-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Data Backup</h1>
        <p className="text-sm text-slate-500 mt-1">
          Snapshot the entire CRM database. Restricted to Super Admin.
        </p>
      </div>

      {/* Hero card */}
      <Card className="border-blue-200 bg-blue-50/60">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-slate-900">Full Database Backup</h2>
              <p className="text-sm text-slate-600 mt-0.5">
                {isLoading ? (
                  <span className="inline-flex items-center gap-2 text-slate-500">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculating…
                  </span>
                ) : (
                  <>
                    <strong>{numberFmt(totalRecords)}</strong> records across{" "}
                    <strong>{numberFmt(totalTables)}</strong> tables total.
                    {hasSplit && (
                      <>
                        {" "}Download in <strong>two parts</strong> for a smoother production experience — collections with more than{" "}
                        <strong>{numberFmt(heavyThreshold)}</strong> records are auto-routed into Part 1.
                      </>
                    )}
                  </>
                )}
              </p>

              {/* Show a single full-DB download when there are no heavy collections */}
              {!hasSplit && !isLoading && (
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    onClick={() => startDownload("csv", "all")}
                    disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="download-all-csv-btn"
                  >
                    {downloading === "all.csv" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                    )}
                    Download as CSV (Excel)
                  </Button>
                  <Button
                    onClick={() => startDownload("json", "all")}
                    disabled={busy}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="download-all-json-btn"
                  >
                    {downloading === "all.json" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download as JSON (restorable)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={fetchSummary}
                    disabled={busy}
                    className="bg-white"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              )}

              {hasSplit && !isLoading && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline"
                    onClick={fetchSummary}
                    disabled={busy}
                    className="bg-white h-9"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              )}

              <p className="text-xs text-slate-500 mt-3">
                <strong className="text-slate-700">CSV</strong> — opens directly in Excel / Google Sheets. Best for viewing &amp; analysis.
                <br />
                <strong className="text-slate-700">JSON</strong> — preserves every detail. Best for technical restore or migration.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auto-assembled backup — client-side orchestrator that side-steps Cloudflare timeouts */}
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-purple-600 flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-semibold text-slate-900">Complete Backup — Auto-assembled</h3>
                <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                  Bypasses Cloudflare limits
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                Downloads every collection individually in the background, then stitches them together into a single ZIP on your device.
                Each request is small &amp; fast, so timeouts never apply — even on very large databases.
              </p>

              {multi.active ? (
                <div className="mt-4 space-y-2" data-testid="auto-assembly-progress">
                  <div className="flex items-center justify-between text-xs text-slate-700">
                    <span>
                      <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />
                      {multi.current
                        ? <>Downloading <strong className="font-mono">{multi.current}</strong>…</>
                        : "Preparing…"}
                    </span>
                    <span className="tabular-nums text-slate-500">
                      {multi.done} / {multi.total}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-purple-100 overflow-hidden">
                    <div
                      className="h-full bg-purple-600 transition-all"
                      style={{ width: `${multi.total ? (multi.done / multi.total) * 100 : 0}%` }}
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={cancelMultiRef}
                    className="h-8 bg-white border-slate-300 text-slate-700 text-xs"
                    data-testid="auto-assembly-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    onClick={() => startAutoAssembledBackup("json")}
                    disabled={isLoading || busy}
                    className="bg-purple-600 hover:bg-purple-700 text-white h-9"
                    data-testid="auto-assembly-json-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Assemble Complete JSON
                  </Button>
                  <Button
                    onClick={() => startAutoAssembledBackup("csv")}
                    disabled={isLoading || busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                    data-testid="auto-assembly-csv-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Assemble Complete CSV
                  </Button>
                </div>
              )}

              {!multi.active && multi.failed && multi.failed.length > 0 && (
                <div className="mt-3 text-xs text-red-600" data-testid="auto-assembly-failures">
                  Last run had {multi.failed.length} failed collection{multi.failed.length !== 1 ? "s" : ""}:
                  {" "}{multi.failed.slice(0, 3).map((f) => f.name).join(", ")}
                  {multi.failed.length > 3 ? ` +${multi.failed.length - 3} more` : ""}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-part cards, only shown when there's at least one heavy collection */}
      {hasSplit && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DownloadPartCard
            part="heavy"
            title="Part 1 · Heavy collections"
            subtitle={`Auto-detected — any table with more than ${numberFmt(heavyThreshold)} records`}
            icon={Layers}
            tint="bg-amber-50 border-amber-200"
            tables={heavyTables}
            records={heavyRecords}
            downloading={downloading}
            busy={busy}
            onDownload={startDownload}
          />
          <DownloadPartCard
            part="light"
            title="Part 2 · Light collections"
            subtitle="Everything else — small reference tables, logs and metadata"
            icon={PackageOpen}
            tint="bg-emerald-50 border-emerald-200"
            tables={lightTables}
            records={lightRecords}
            downloading={downloading}
            busy={busy}
            onDownload={startDownload}
          />
        </div>
      )}

      {/* Two info cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-slate-900">What's inside?</h3>
            </div>
            <ul className="space-y-1.5 text-sm text-slate-600 list-disc pl-5">
              <li>Every lead, requirement, sample, order, follow-up and call record.</li>
              <li>One file per collection — open in Excel, Google Sheets, or any database tool.</li>
              <li>
                A summary file <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">_manifest.json</code> listing all tables and counts.
              </li>
              {hasSplit && (
                <li className="text-slate-500">
                  Part 1 + Part 2 together contain the complete database — no rows are omitted.
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-900">Security</h3>
            </div>
            <ul className="space-y-1.5 text-sm text-slate-600 list-disc pl-5">
              <li>Only Super Admin users can download.</li>
              <li>Every download is logged with your name, time, and IP address.</li>
              <li>Store the file in a safe location — it contains business data.</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Table listing */}
      <Card className="border-slate-200">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">What will be included</h3>
          <div className="text-xs text-slate-500">
            {numberFmt(totalTables)} tables · {numberFmt(totalRecords)} records
          </div>
        </div>
        <div className="border-t border-slate-100">
          {isLoading ? (
            <div className="py-10 text-center text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2.5 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Table</th>
                  <th className="text-left py-2.5 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Part</th>
                  <th className="text-right py-2.5 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Records</th>
                  <th className="text-right py-2.5 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(summary?.tables || []).map((t) => {
                  const isHeavy = t.count > heavyThreshold;
                  const busyJson = downloading === `col.${t.name}.json`;
                  const busyCsv = downloading === `col.${t.name}.csv`;
                  return (
                    <tr key={t.name} className="hover:bg-slate-50" data-testid={`backup-table-row-${t.name}`}>
                      <td className="py-2 px-5 font-mono text-xs text-slate-700">{t.name}</td>
                      <td className="py-2 px-5">
                        <span className={`inline-block text-[10px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded ${
                          isHeavy ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                        }`}>
                          {isHeavy ? "Part 1 · Heavy" : "Part 2 · Light"}
                        </span>
                      </td>
                      <td className="py-2 px-5 text-right text-slate-700 tabular-nums">
                        {numberFmt(t.count)}
                      </td>
                      <td className="py-2 px-5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => startCollectionDownload(t.name, "csv")}
                            disabled={busy || t.count === 0}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={`Download ${t.name} as CSV`}
                            data-testid={`row-download-csv-${t.name}`}
                          >
                            {busyCsv ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <FileSpreadsheet className="w-3 h-3" />
                            )}
                            CSV
                          </button>
                          <button
                            type="button"
                            onClick={() => startCollectionDownload(t.name, "json")}
                            disabled={busy || t.count === 0}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={`Download ${t.name} as JSON`}
                            data-testid={`row-download-json-${t.name}`}
                          >
                            {busyJson ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3" />
                            )}
                            JSON
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(!summary?.tables || summary.tables.length === 0) && (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-slate-400 text-sm">
                      No collections found in the database.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Backup;
